/**
 * EnvironmentEffects — sky dome, stars, ambient particles, and the market's
 * weather: the FOG ROLL.
 *
 * Sky presets: starfield, sunset, storm, void, aurora
 * Particle types: dust, embers, snow, fireflies, ash, magic
 *
 * Sky dome uses TSL NodeMaterial — no manual time uniform needed.
 */

import * as THREE from 'three/webgpu';
import {
  Fn, vec2, vec3, float,
  uniform, normalize, positionWorld, time,
  max, pow, mix, smoothstep, step, floor, fract, sin, dot, length, oneMinus, fwidth,
  color as colorNode, instancedBufferAttribute
} from 'three/tsl';
import { getMutationFloorY } from '@chaos/sim';
import { softCircle, createParticleMaterial } from './vfx/ParticleUtils.js';
import { getCurrentTier } from './PostProcessing.js';
import { setDeckHorizon } from './SurfaceShaders.js';

let particleSystem = null;
let particleVelocities = null;
let particleType = 'dust';
let skyDome = null;
let starField = null;
let _scene = null;

const PARTICLE_COUNT = 250;
const PARTICLE_SPREAD = 60;

/**
 * Fraction of the ambient field simulated and drawn per quality tier.
 *
 * The buffers are always allocated for the full count — resizing an
 * InstancedBufferAttribute at runtime means a new GPU buffer, and the tier can
 * change mid-match when the governor reacts to a frame-rate dip. Only the
 * active prefix is stepped and drawn, so the cost scales without reallocating.
 * This field used to be stepped in full at every tier: a 250-iteration loop
 * plus a 750-float upload every frame on the phone tiers, for haze nobody is
 * looking at during a fight.
 */
const TIER_AMBIENT = { ultra: 1, high: 0.7, medium: 0.4, low: 0.2 };

/** Fog cloud layers drawn per tier: overdraw is the phone's killer. */
const TIER_CLOUDS = { ultra: 3, high: 3, medium: 2, low: 1 };

// ─── Shared Noise (TSL) ────────────────────────────────────

// Hoskins' hash12, not `fract(sin(...))`: precise at world-coordinate
// magnitudes, where Metal's fast `sin` skews the classic one. Same reason as
// SurfaceShaders.
const hash2d = Fn(([p_immutable]) => {
  const p = fract(vec2(p_immutable).mul(vec2(0.1031, 0.1030))).toVar();
  p.addAssign(dot(p, p.yx.add(33.33)));
  return fract(p.x.add(p.y).mul(p.x));
});

const noise2d = Fn(([p_immutable]) => {
  const p = vec2(p_immutable).toVar();
  const i = floor(p);
  const f = fract(p);
  const ff = f.mul(f).mul(f.mul(-2.0).add(3.0));
  const a = hash2d(i);
  const b = hash2d(i.add(vec2(1.0, 0.0)));
  const c = hash2d(i.add(vec2(0.0, 1.0)));
  const d = hash2d(i.add(vec2(1.0, 1.0)));
  return mix(mix(a, b, ff.x), mix(c, d, ff.x), ff.y);
});

// ─── Sky Dome ───────────────────────────────────────────────

/**
 * v2 has no purple. The `void` and `aurora` presets used to bottom out in a
 * violet-navy ([0.05, 0.02, 0.1] and [0.1, 0.05, 0.2]) that showed past the
 * deck edge on The Chart, HFT Floor and Bubble — v1's brand, on every frame
 * of three arenas. The palette is the terminal's now: near-black with a
 * green-grey cast, and at most a cyan cast toward the horizon. `sky-palette
 * .test.js` pins every preset colour outside the purple band.
 */
export const SKY_PRESETS = {
  // The graybox. Navy-grey, hueless enough to sit under any theme.
  starfield: {
    topColor: [0.004, 0.006, 0.010],
    midColor: [0.014, 0.020, 0.028],
    bottomColor: [0.040, 0.048, 0.058],
    cloudBand: 0,
    starsVisible: true,
    starBrightness: 1.0,
  },
  sunset: {
    topColor: [0.045, 0.05, 0.055],
    midColor: [0.6, 0.2, 0.1],
    bottomColor: [0.9, 0.4, 0.15],
    cloudBand: 0.6,
    starsVisible: false,
    starBrightness: 0,
  },
  storm: {
    topColor: [0.05, 0.055, 0.06],
    midColor: [0.13, 0.135, 0.14],
    bottomColor: [0.2, 0.17, 0.14],
    cloudBand: 1.0,
    starsVisible: false,
    starBrightness: 0,
  },
  // The terminal: near-black, green-grey. The Chart and HFT Floor.
  void: {
    topColor: [0.004, 0.007, 0.007],
    midColor: [0.012, 0.022, 0.022],
    bottomColor: [0.034, 0.052, 0.050],
    cloudBand: 0,
    starsVisible: true,
    starBrightness: 0.6,
  },
  // Bubble: its floor is the void and reads as pale sky rather than a dark
  // hole, so this one is lighter toward the horizon — a cyan-grey, never violet.
  aurora: {
    topColor: [0.015, 0.04, 0.045],
    midColor: [0.06, 0.13, 0.14],
    bottomColor: [0.16, 0.24, 0.25],
    cloudBand: 0.3,
    starsVisible: true,
    starBrightness: 0.8,
  },
};

// Sky uniforms (TSL uniform nodes)
const _topColor = uniform(new THREE.Color(0x1a3050));
const _bottomColor = uniform(new THREE.Color(0x2a3a5e));
const _midColor = uniform(new THREE.Color(0x1e2d48));
const _offset = uniform(20);
const _exponent = uniform(0.6);
const _cloudBand = uniform(0.0);

export function createSkyDome(scene) {
  _scene = scene;
  const skyGeo = new THREE.SphereGeometry(400, 24, 24);
  const skyMat = new THREE.NodeMaterial();
  skyMat.side = THREE.BackSide;
  skyMat.depthWrite = false;
  // Material.fog defaults to true, and FogExp2(0.009) at this dome's 400-unit
  // radius is ~97% fog — every authored preset gradient rendered as one flat
  // fog color. The sky IS the backdrop; it must never be fogged.
  skyMat.fog = false;

  // TSL fragment: sky gradient with cloud band
  const skyColor = Fn(() => {
    const worldPos = positionWorld;
    const h = normalize(worldPos.add(vec3(0, _offset, 0))).y;
    const t = max(pow(max(h, 0.0), _exponent), 0.0);

    // Three-color gradient: bottom → mid → top
    const lowBlend = mix(_bottomColor.toVec3(), _midColor.toVec3(), t.div(0.4));
    const highBlend = mix(_midColor.toVec3(), _topColor.toVec3(), t.sub(0.4).div(0.6));
    const color = mix(lowBlend, highBlend, smoothstep(0.39, 0.41, t)).toVar();

    // Cloud band at horizon
    const band = smoothstep(0.0, 0.15, h).mul(smoothstep(0.3, 0.15, h));
    const n = noise2d(worldPos.xz.mul(0.015)).mul(0.7).add(noise2d(worldPos.xz.mul(0.03)).mul(0.3));
    color.assign(mix(color, vec3(1.0), band.mul(n).mul(_cloudBand).mul(0.25)));

    return color;
  })();

  skyMat.colorNode = skyColor;

  skyDome = new THREE.Mesh(skyGeo, skyMat);
  scene.add(skyDome);

  // Star layer
  createStarField(scene);

  if (scene.fog) {
    _fogBase.density = scene.fog.density;
    _fogBase.color.copy(scene.fog.color);
  }
  initFogClouds(scene);

  return skyDome;
}

const _starOpacity = uniform(0);
/** The ambient field's single upload range, reused every frame. */
const _ambientRange = { start: 0, count: 0 };

function createStarField(scene) {
  const count = 500;
  const positions = new Float32Array(count * 3);
  const sizes = new Float32Array(count);

  for (let i = 0; i < count; i++) {
    const theta = Math.random() * Math.PI * 2;
    const phi = Math.acos(Math.random() * 0.8 + 0.2);
    const r = 390;
    positions[i * 3] = r * Math.sin(phi) * Math.cos(theta);
    positions[i * 3 + 1] = r * Math.cos(phi);
    positions[i * 3 + 2] = r * Math.sin(phi) * Math.sin(theta);
    // Mostly dust with a few standouts — uniform sizing read as a repeating
    // pattern rather than a night sky.
    const roll = Math.random();
    sizes[i] = roll > 0.94 ? 2.2 + Math.random() * 1.6 : 0.4 + Math.random() * 1.1;
  }

  const posAttr = new THREE.InstancedBufferAttribute(positions, 3);
  const sizeAttr = new THREE.InstancedBufferAttribute(sizes, 1);
  const geo = new THREE.PlaneGeometry(1, 1);
  geo.setAttribute('starPos', posAttr);
  geo.setAttribute('starSize', sizeAttr);

  const material = new THREE.SpriteNodeMaterial();
  material.positionNode = instancedBufferAttribute(posAttr);
  material.scaleNode = instancedBufferAttribute(sizeAttr);
  material.colorNode = colorNode(0xffffff);
  material.opacityNode = softCircle().mul(_starOpacity);
  material.transparent = true;
  material.depthWrite = false;
  material.blending = THREE.AdditiveBlending;
  // Additive + fog ADDS fog color at distance instead of fading — and at 390
  // units the stars were pure fog. Same reasoning as the sky dome.
  material.fog = false;

  starField = new THREE.Mesh(geo, material);
  starField.count = count;
  starField.frustumCulled = false;
  scene.add(starField);
}

// The fog must agree with the horizon or distance reads as dirt instead of
// atmosphere: a sunset sky over v1's fixed navy fog put a grey-blue smear
// under a peach horizon. Fog tracks the preset's low sky, darkened so fogged
// geometry recedes rather than glows. The deck's rim sinks to the same colour.
const _fogBase = { color: new THREE.Color(0x14162e), density: 0.005 };

function syncFogToSky() {
  const horizon = _bottomColor.value.clone().lerp(_midColor.value, 0.35).multiplyScalar(0.85);
  _fogBase.color.copy(horizon);
  setDeckHorizon(horizon);
  if (!_scene) return;
  if (_scene.background && _scene.background.isColor) _scene.background.copy(horizon);
  applyFog();
}

export function updateSkyColors(skyColor, fogColor, skyPreset) {
  if (!skyDome) return;

  const preset = skyPreset && SKY_PRESETS[skyPreset];
  if (preset) {
    _topColor.value.setRGB(...preset.topColor);
    _midColor.value.setRGB(...preset.midColor);
    _bottomColor.value.setRGB(...preset.bottomColor);
    _cloudBand.value = preset.cloudBand;
    syncFogToSky();

    if (starField) {
      _starOpacity.value = preset.starsVisible ? preset.starBrightness : 0;
      starField.visible = preset.starsVisible;
    }
  } else {
    const top = new THREE.Color(skyColor);
    const bottom = fogColor ? new THREE.Color(fogColor) : top.clone().multiplyScalar(0.6);
    const mid = top.clone().lerp(bottom, 0.5);
    _topColor.value.copy(top);
    _midColor.value.copy(mid);
    _bottomColor.value.copy(bottom);
    _cloudBand.value = 0;
    syncFogToSky();

    // Auto-enable stars for dark skies
    if (starField) {
      const brightness = top.r * 0.299 + top.g * 0.587 + top.b * 0.114;
      _starOpacity.value = brightness < 0.15 ? 0.7 : 0;
      starField.visible = brightness < 0.15;
    }
  }
}

// ─── Fog roll ───────────────────────────────────────────────

/**
 * FOG, the market event, in the pass-2 language: not a grey veil that hides
 * the fight, but stylised CEL CLOUD LAYERS rolling across the deck — flat
 * sheets a little above the floor, each blob a two-band cloud with an ink
 * rim, drifting on a fixed wind, plus a MODEST rise in the scene fog so the
 * far rim recedes. Bodies stand in it up to the knee; the deck reads through.
 *
 * Arrival: the front ROLLS in from one side of the arena (a `roll` uniform
 * sweeps the mask edge across, with a white crest on the front) over
 * FOG_ROLL_MS. Removal: noise EROSION with a bright edge. All uniform-driven.
 *
 * Built once at boot (one material, three meshes), laid out per map, hidden
 * until a roll, and PREWARMED by ArenaRenderer with the floor overlays.
 */
const CLOUD_GREY = new THREE.Color(0.62, 0.68, 0.70);
const CLOUD_WIND = new THREE.Vector2(0.8, 0.6).normalize();
/** Heights above the floor of the three layers: ankle, shin, knee. */
const CLOUD_LAYERS = [0.35, 0.8, 1.3];

const _cloudDensity = uniform(0);
const _cloudRoll = uniform(0);
const _cloudErode = uniform(-0.15);
const _cloudCenter = uniform(new THREE.Vector2(0, 0));
const _cloudRadius = uniform(45);
const _cloudFloor = uniform(0);
const _clouds = [];
const _fogRoll = { density: 0, roll: 0, erode: 0 };

function cloudMaterial() {
  const m = new THREE.MeshBasicNodeMaterial({ transparent: true, depthWrite: false });
  // They ARE the fog; fogging them double-dips.
  m.fog = false;
  const p = vec2(positionWorld.x, positionWorld.z);
  const rel = p.sub(_cloudCenter);
  const w = vec2(CLOUD_WIND.x, CLOUD_WIND.y);
  // Each layer sits at its own height, which offsets its noise and raises
  // its threshold: three different clouds from one material, the higher the
  // sparser. The first cut stacked three dense sheets and whited the deck out.
  const h = positionWorld.y.sub(_cloudFloor);
  const layerT = smoothstep(float(0.2), float(1.4), h);
  const q = p.mul(0.045).add(w.mul(time.mul(0.45))).add(h.mul(3.1));
  const n = noise2d(q).mul(0.6).add(noise2d(q.mul(2.3).add(5.0)).mul(0.4));
  // The rolling front, along the wind, from -1.2R to +1.4R.
  const edgeD = dot(rel, w);
  const rollEdge = _cloudRoll.mul(2.6).sub(1.2).mul(_cloudRadius);
  const present = oneMinus(smoothstep(rollEdge.sub(4.0), rollEdge, edgeD));
  const crest = smoothstep(rollEdge.sub(7.0), rollEdge.sub(4.0), edgeD).mul(present);
  // Erosion on removal.
  const alive = smoothstep(_cloudErode.sub(0.03), _cloudErode.add(0.03), n);
  const eFront = smoothstep(_cloudErode.sub(0.12), _cloudErode, n).mul(oneMinus(smoothstep(_cloudErode, _cloudErode.add(0.05), n)));
  // Two cel bands and an ink rim, thresholds rising with the layer.
  const t1 = float(0.45).add(layerT.mul(0.13));
  const lvl1 = step(t1, n);
  const lvl2 = step(t1.add(0.14), n);
  // An ink rim a couple of pixels wide, however flat the noise is there.
  const rimW = max(fwidth(n).mul(1.6), 0.012);
  const rim = step(t1.sub(rimW), n).sub(lvl1);
  const rimFade = oneMinus(smoothstep(_cloudRadius.mul(1.05), _cloudRadius.mul(1.35), length(rel)));
  const light = vec3(0.74, 0.81, 0.85);
  const core = vec3(0.88, 0.92, 0.94);
  const inkTeal = vec3(0.13, 0.20, 0.24);
  let color = mix(light, core, lvl2);
  color = mix(color, inkTeal, rim);
  color = mix(color, vec3(1.0), max(crest, eFront).mul(0.8));
  m.colorNode = color;
  // Each layer alone is a light wash; where two blobs stack the deck still
  // reads. The ink rim is the darkest thing in it.
  m.opacityNode = lvl1.mul(0.20).add(lvl2.mul(0.10)).add(rim.mul(0.34)).add(crest.mul(lvl1).mul(0.25))
    .min(0.5)
    .mul(_cloudDensity.mul(0.5).add(0.5))
    .mul(present).mul(alive).mul(rimFade)
    .mul(step(float(0.001), _cloudDensity));
  return m;
}

function initFogClouds(scene) {
  if (_clouds.length) return;
  const material = cloudMaterial();
  for (let i = 0; i < CLOUD_LAYERS.length; i++) {
    const mesh = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), material);
    mesh.rotation.x = -Math.PI / 2;
    mesh.renderOrder = 3;
    mesh.frustumCulled = false;
    mesh.visible = false;
    mesh.name = `fog-cloud-${i}`;
    scene.add(mesh);
    _clouds.push(mesh);
  }
}

/**
 * Sizes and centres the layers on a map. Called by ArenaRenderer.build with
 * the height of the drawn deck; without one, the sim's nominal floor.
 */
export function layoutFogClouds(map, groundY = null) {
  if (!map) return;
  _cloudCenter.value.set(map.centerX ?? 0, map.centerZ ?? 0);
  _cloudRadius.value = map.startRadius ?? 45;
  const floorY = groundY ?? (map.floor?.kind === 'none' ? (getMutationFloorY(map) ?? 0) : (map.floor?.y ?? 0));
  _cloudFloor.value = floorY;
  const size = (map.startRadius ?? 45) * 2.8;
  for (let i = 0; i < _clouds.length; i++) {
    const mesh = _clouds[i];
    mesh.position.set(map.centerX ?? 0, floorY + CLOUD_LAYERS[i], map.centerZ ?? 0);
    mesh.scale.set(size, size, 1);
  }
}

/** The cloud meshes, for the prewarm. */
export function fogCloudMeshes() {
  return _clouds;
}

function applyFog() {
  if (!_scene?.fog) return;
  // 0.005 → 0.013 at full density: the far rim recedes into weather while
  // the fight, within ~25 units, keeps its contrast. The clouds are the read.
  const d = _fogRoll.density;
  _scene.fog.density = _fogBase.density * (1 + 1.3 * d);
  _scene.fog.color.copy(_fogBase.color).lerp(CLOUD_GREY, d * 0.4);
}

/**
 * Drives the roll. Density and colour are `reference` uniforms on the scene
 * fog in three r183, so this is a uniform write, never a program change —
 * replacing the fog OBJECT would recompile every fogged material in the arena.
 */
export function setFogRoll({ density = 0, roll = 0, erode = 0 } = {}) {
  _fogRoll.density = density;
  _fogRoll.roll = roll;
  _fogRoll.erode = erode;
  _cloudDensity.value = density;
  _cloudRoll.value = roll;
  _cloudErode.value = -0.15 + Math.max(0, Math.min(1, erode)) * 1.3;
  const layers = TIER_CLOUDS[getCurrentTier()] ?? 3;
  for (let i = 0; i < _clouds.length; i++) _clouds[i].visible = density > 0.001 && i < layers;
  applyFog();
}

export function fogRollState() {
  return _fogRoll;
}

// ─── Particles ──────────────────────────────────────────────

function getParticleConfig(type) {
  switch (type) {
    case 'embers':
      return { color: 0xff6622, size: 0.25, velocityY: [0.5, 1.5], velocityXZ: 0.3, opacity: 0.7, blending: 'additive' };
    case 'snow':
      return { color: 0xffffff, size: 0.2, velocityY: [-0.8, -0.3], velocityXZ: 0.5, opacity: 0.6, blending: 'normal' };
    case 'fireflies':
      return { color: 0x88ff44, size: 0.3, velocityY: [-0.2, 0.2], velocityXZ: 0.4, opacity: 0.8, blending: 'additive' };
    case 'ash':
      return { color: 0x888888, size: 0.15, velocityY: [-0.5, -0.1], velocityXZ: 0.3, opacity: 0.4, blending: 'normal' };
    case 'magic':
      // Drifting motes on the void and aurora skies. Faint GOLD, alpha-blended:
      // the old 0xaa66ff additive orbs were the one purple thing on every
      // frame of The Chart, and purple is v1's brand, never v2's.
      return { color: 0xffd98a, size: 0.13, velocityY: [0.1, 0.4], velocityXZ: 0.5, opacity: 0.32, blending: 'normal' };
    case 'dust':
    default:
      return { color: 0xffffff, size: 0.12, velocityY: [-0.1, 0.1], velocityXZ: 0.2, opacity: 0.35, blending: 'normal' };
  }
}

export function initParticles(scene, type = 'dust') {
  disposeParticles(scene);

  particleType = type;
  const config = getParticleConfig(type);

  const positions = new Float32Array(PARTICLE_COUNT * 3);
  particleVelocities = new Float32Array(PARTICLE_COUNT * 3);

  for (let i = 0; i < PARTICLE_COUNT; i++) {
    const i3 = i * 3;
    positions[i3] = (Math.random() - 0.5) * PARTICLE_SPREAD;
    positions[i3 + 1] = Math.random() * 30 + 2;
    positions[i3 + 2] = (Math.random() - 0.5) * PARTICLE_SPREAD;

    particleVelocities[i3] = (Math.random() - 0.5) * config.velocityXZ;
    particleVelocities[i3 + 1] = config.velocityY[0] + Math.random() * (config.velocityY[1] - config.velocityY[0]);
    particleVelocities[i3 + 2] = (Math.random() - 0.5) * config.velocityXZ;
  }

  const posAttr = new THREE.InstancedBufferAttribute(positions, 3);
  const geo = new THREE.PlaneGeometry(1, 1);
  geo.setAttribute('particlePos', posAttr);

  const material = createParticleMaterial(posAttr, config);
  // Half these configs blend additively (embers, fireflies, magic), where fog
  // adds its color rather than fading; the rest live within ~30 units of the
  // camera where FogExp2(0.009) is negligible anyway. Off for all of them.
  material.fog = false;

  particleSystem = new THREE.Mesh(geo, material);
  particleSystem.count = PARTICLE_COUNT;
  particleSystem.frustumCulled = false;
  scene.add(particleSystem);
}

/**
 * LOW GRAVITY's ambient cue: the motes drift UP while gravity is low, the
 * cheapest "something about the air changed" there is — a velocity bias on
 * the field that is already being stepped, no reallocation, no new draws.
 */
let _ambientGravity = 1;
export function setAmbientGravity(multiplier) {
  _ambientGravity = Math.max(0, Math.min(1, multiplier ?? 1));
}

export function updateEnvironmentEffects(delta, cameraPosition) {
  if (!particleSystem || !particleVelocities) return;

  const posAttr = particleSystem.geometry.attributes.particlePos;
  const positions = posAttr.array;
  const now = Date.now() * 0.001;

  const active = Math.max(1, Math.round(PARTICLE_COUNT * (TIER_AMBIENT[getCurrentTier()] ?? 1)));
  // Only the active prefix is drawn; the rest of the buffer keeps its last
  // positions so a tier change mid-match brings particles back where they
  // were rather than snapping them to the origin.
  particleSystem.count = active;
  const lift = (1 - _ambientGravity) * 0.9;

  for (let i = 0; i < active; i++) {
    const i3 = i * 3;
    positions[i3] += particleVelocities[i3] * delta;
    positions[i3 + 1] += (particleVelocities[i3 + 1] + lift) * delta;
    positions[i3 + 2] += particleVelocities[i3 + 2] * delta;

    // Fireflies: sine-wave wander
    if (particleType === 'fireflies') {
      positions[i3] += Math.sin(now * 1.5 + i * 0.7) * 0.02;
      positions[i3 + 2] += Math.cos(now * 1.3 + i * 0.9) * 0.02;
    }

    // Keep particles centered around camera
    if (cameraPosition) {
      const dx = positions[i3] - cameraPosition.x;
      const dz = positions[i3 + 2] - cameraPosition.z;
      const halfSpread = PARTICLE_SPREAD / 2;
      if (dx > halfSpread) positions[i3] -= PARTICLE_SPREAD;
      if (dx < -halfSpread) positions[i3] += PARTICLE_SPREAD;
      if (dz > halfSpread) positions[i3 + 2] -= PARTICLE_SPREAD;
      if (dz < -halfSpread) positions[i3 + 2] += PARTICLE_SPREAD;
    }

    // Vertical wrap
    if ((particleType === 'embers' || particleType === 'magic') && positions[i3 + 1] > 35) {
      positions[i3 + 1] = 0;
    } else if ((particleType === 'snow' || particleType === 'ash') && positions[i3 + 1] < -1) {
      positions[i3 + 1] = 30;
    } else if (positions[i3 + 1] > 35 || positions[i3 + 1] < -1) {
      positions[i3 + 1] = Math.random() * 30;
    }
  }

  // Upload only what moved. Without a range the driver copies all 750 floats
  // every frame even when the low tier is simulating 50 of them.
  // One range object for the life of the buffer: `addUpdateRange` allocates,
  // and the backend empties the list after every upload.
  if (posAttr.updateRanges.length === 0) posAttr.updateRanges.push(_ambientRange);
  _ambientRange.count = active * 3;
  posAttr.needsUpdate = true;

  // Animate star twinkle
  if (starField?.visible) {
    starField.rotation.y += delta * 0.002;
  }
}

export function disposeParticles(scene) {
  if (particleSystem) {
    scene.remove(particleSystem);
    particleSystem.geometry.dispose();
    particleSystem.material.dispose();
    particleSystem = null;
    particleVelocities = null;
  }
}

export function selectParticleType(floorType, environment) {
  if (floorType === 'lava') return 'embers';

  if (environment?.skyColor) {
    const sky = new THREE.Color(environment.skyColor);
    if (sky.b > 0.4 && sky.r < 0.3 && sky.g < 0.3) return 'snow';
    if (sky.r > 0.3 && sky.b < 0.15) return 'embers';
  }

  if (environment?.skyPreset) {
    const PRESET_PARTICLES = {
      aurora: 'magic',
      void: 'magic',
      storm: 'ash',
      sunset: 'fireflies',
    };
    return PRESET_PARTICLES[environment.skyPreset] ?? 'dust';
  }

  return 'dust';
}
